import { useState } from "react";
import { Globe, ShieldCheck, Network, Server, Cpu, Monitor, Plus } from "lucide-react";
import { SOFTWARE_STACK } from "./data";

const Node = ({ icon: Icon, title, sub, accent = false, dashed = false, testId }) => (
  <div
    className={`relative flex items-center gap-3 px-4 py-3 rounded-lg border bg-[#0A1628] ${dashed ? "border-dashed border-[#5A6578]" : accent ? "border-[#C9A962]/60 shadow-[0_0_24px_rgba(201,169,98,0.12)]" : "border-[#2A3648]"}`}
    data-testid={testId}
  >
    <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${dashed ? "bg-[#1A2A40]" : "bg-[#C9A962]/10"}`}>
      <Icon size={18} className={dashed ? "text-[#5A6578]" : "text-[#C9A962]"} />
    </div>
    <div className="min-w-0">
      <p className={`text-sm font-semibold leading-tight ${dashed ? "text-[#A0ABC0]" : "text-white"}`}>{title}</p>
      {sub && <p className="text-[11px] text-[#A0ABC0] leading-snug mt-0.5">{sub}</p>}
    </div>
  </div>
);

const Link = ({ label }) => (
  <div className="flex flex-col items-center py-1">
    <div className="flow-line h-8" />
    {label && <span className="text-[10px] font-mono text-[#C9A962]/80 tracking-wider">{label}</span>}
  </div>
);

const Topology = () => (
  <div className="keep-dark p-6 md:p-10 bg-[#121E30] rounded-xl border border-[#2A3648]" data-testid="coe-topology">
    <div className="max-w-xs mx-auto">
      <Node icon={Globe} title="Campus WAN / Internet" sub="Secure uplink" testId="arch-node-internet" />
      <Link />
      <Node icon={ShieldCheck} title="Perimeter Firewall" sub="Security gateway & admin console" testId="arch-node-firewall" />
      <Link label="10 GbE" />
      <Node icon={Network} title="Layer-3 Core Switch" sub="Top-of-rack routing" accent testId="arch-node-core" />
    </div>
    <div className="hidden md:block relative h-10 max-w-3xl mx-auto">
      <div className="absolute left-1/4 right-1/4 top-1/2 h-px bg-[#C9A962]/40" />
      <div className="absolute left-1/2 top-0 h-1/2 w-px bg-[#C9A962]/40" />
      <div className="absolute left-1/4 top-1/2 h-1/2 w-px bg-[#C9A962]/40" />
      <div className="absolute right-1/4 top-1/2 h-1/2 w-px bg-[#C9A962]/40" />
    </div>
    <div className="md:hidden"><Link /></div>
    <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
      <div className="p-4 rounded-xl border border-[#C9A962]/25 bg-[#C9A962]/[0.03]" data-testid="arch-group-cluster">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A962] mb-3">HPC / AI Cluster · Enterprise Rack</p>
        <div className="space-y-3">
          <Node icon={Server} title="Master Node" sub="Orchestration, scheduling & storage" accent />
          <Node icon={Cpu} title="Compute Nodes" sub="Intel® Xeon® · scale-out" />
        </div>
      </div>
      <div className="p-4 rounded-xl border border-[#2A3648]" data-testid="arch-group-lab">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#A0ABC0] mb-3">Student Lab Pod</p>
        <div className="space-y-3">
          <Node icon={Network} title="Infrastructure Switch" sub="10G aggregation & distribution" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Node icon={Monitor} title="Access 1" sub="Lab seats" />
            <Node icon={Monitor} title="Access 2" sub="Lab seats" />
          </div>
          <Node icon={Plus} title="Add access modules" sub="Expand as programs grow" dashed />
        </div>
      </div>
    </div>
  </div>
);

const Stack = () => (
  <div className="keep-dark p-6 md:p-10 bg-[#121E30] rounded-xl border border-[#2A3648] space-y-3" data-testid="coe-stack">
    {SOFTWARE_STACK.map((l, i) => (
      <div
        key={l.id}
        className="grid md:grid-cols-[220px_1fr] items-center gap-3 p-4 rounded-lg border border-[#2A3648] bg-[#0A1628]"
        style={{ borderLeft: `3px solid rgba(201,169,98,${1 - i * 0.17})` }}
      >
        <p className="text-sm font-semibold"><span className="font-mono text-[#C9A962] text-xs mr-2">L{SOFTWARE_STACK.length - i}</span>{l.layer}</p>
        <div className="flex flex-wrap gap-2">
          {l.items.map((it) => (
            <span key={it} className="text-xs px-3 py-1 rounded-full bg-[#1A2A40] border border-[#2A3648] text-[#A0ABC0]">{it}</span>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const VIEWS = [
  { id: "topology", label: "Infrastructure Topology" },
  { id: "stack", label: "Layered Lab Stack" },
];

export const Architecture = () => {
  const [view, setView] = useState("topology");
  return (
    <div>
      <div className="inline-flex p-1 mb-6 rounded-full bg-[#121E30] border border-[#2A3648]" role="tablist">
        {VIEWS.map((v) => (
          <button
            key={v.id}
            type="button"
            role="tab"
            aria-selected={view === v.id}
            onClick={() => setView(v.id)}
            className={`px-4 py-2 text-xs md:text-sm rounded-full transition-colors ${view === v.id ? "bg-[#C9A962] text-[#0A1628] font-semibold" : "text-[#A0ABC0] hover:text-white"}`}
            data-testid={`arch-tab-${v.id}`}
          >
            {v.label}
          </button>
        ))}
      </div>
      {view === "topology" ? <Topology /> : <Stack />}
      <p className="text-[#5A6578] text-xs mt-4">Conceptual reference architecture. Final configuration is tailored to each institution in the detailed proposal.</p>
    </div>
  );
};
